"use client";

import { useState } from "react";
import { AuthGatewayProviders } from "@miadvg/auth-gateway/next-client";
import { ConfigProvider } from "@miadvg/config";
import { PostHogProvider } from "@miadvg/posthog";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  CLIENT_CONTEXT_STAFF_APP_SLUG,
  CLIENT_CONTEXT_STAFF_AUTH_CALLBACK_PATH,
  CLIENT_CONTEXT_STAFF_SIGN_OUT_PATH,
} from "@/lib/auth/staff-shared";
import { Toaster } from "@/components/ui/sonner";

interface ProvidersProps {
  children: React.ReactNode;
}

/**
 * Client-side providers wrapper
 *
 * ConfigProvider: Fetches runtime config from /api/config
 * PostHogProvider: Initializes PostHog after config is ready
 *
 * Product events: `captureEvent`. Feature flags: `useServiceFlagEnabled` /
 * `createServiceFlagHooks` from `@miadvg/posthog`. Raw `posthog-js/react` only
 * when you need APIs this package does not wrap (e.g. `usePostHog`).
 */
export function Providers({ children }: ProvidersProps) {
  // QueryClient must live in component state so each request gets its own
  // instance during SSR/streaming and we don't share a cache across users.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );

  return (
    <ConfigProvider>
      <PostHogProvider>
        <AuthGatewayProviders
          staff={{
            appSlug: CLIENT_CONTEXT_STAFF_APP_SLUG,
            callbackPath: CLIENT_CONTEXT_STAFF_AUTH_CALLBACK_PATH,
            signOutPath: CLIENT_CONTEXT_STAFF_SIGN_OUT_PATH,
          }}
        >
          <QueryClientProvider client={queryClient}>
            {children}
            <Toaster />
          </QueryClientProvider>
        </AuthGatewayProviders>
      </PostHogProvider>
    </ConfigProvider>
  );
}
